import { stringToColor } from 'utils/strings/strings';
import { Point } from 'api/grpc/resources';
import { CanvasRegionBaseConfig, MaskConfig, PolygonConfig, RectConfig } from 'modules/Canvas/helpers/types';
import { UiRegionItem } from 'utils/cfUiItems/base';

// GRPC
export function grpcToRestPointData(point: Point | undefined): CF.API.Point | undefined {
  if (!point) {
    return undefined;
  }

  return {
    row: point?.getRow() || 0,
    col: point?.getCol() || 0,
    z: 0,
  };
}

// GRPC -> UI
export function convertPointAPIRegionToShapeProps(region: CF.API.RegionsEntity, colorOverride?: string): CF.API.PointShapeProps {
  if (!isPointRegion(region.region_info)) {
    throw new Error('Region info is not a point region');
  }

  return {
    point: {
      x: region.region_info.point.col,
      y: region.region_info.point.row,
    },
    color: colorOverride || stringToColor(region.data.concepts?.[0]?.id || region.id),
  };
}

export function isPointRegion(regionInfo: CF.API.RegionInfo): regionInfo is CF.API.PointRegionInfo {
  return 'point' in regionInfo;
}

export function isPointShapeProps(shapeProps: CF.API.ShapeProps): shapeProps is CF.API.ShapeProps {
  return !!shapeProps && 'point' in shapeProps;
}

export function isPointUIRegionItem(
  regionData: CF.API.BboxShapeProps | CF.API.PolygonShapeProps | CF.API.MaskShapeProps | CF.API.PointShapeProps,
): regionData is CF.API.PointShapeProps {
  return 'point' in regionData;
}

export const getPointRegionInfo = (regionData: CF.API.PointShapeProps): CF.API.RegionInfo => {
  const x = Number(regionData.point.x);
  const y = Number(regionData.point.y);

  // Add z coordinate as required by the API
  // Note: col corresponds to x (horizontal), row corresponds to y (vertical)
  return {
    point: {
      row: y,
      col: x,
      z: 0,
    },
  };
};

// Canvas specific
export interface PointShapeProps {
  point: {
    x: number;
    y: number;
  };
  color?: string;
  positive?: boolean;
}

export interface PointCanvasConfig extends CanvasRegionBaseConfig {
  x: number;
  y: number;
  radius: number;
  color: string;
  zoomScale?: number;
  positive?: boolean;
}
export interface BoxCanvasConfig extends CanvasRegionBaseConfig {
  x: number;
  y: number;
  color: string;
  zoomScale?: number;
  id?: string;
  size?: number;
  stroke?: string;
  strokeWidth?: number;
}

export function isPointCanvasConfig(shapeConfig: RectConfig | PolygonConfig | MaskConfig | PointCanvasConfig): shapeConfig is PointCanvasConfig {
  return 'x' in shapeConfig && 'y' in shapeConfig && 'radius' in shapeConfig && 'color' in shapeConfig;
}

export const isPositivePointCanvasConfig = (pointCanvasConfig: PointCanvasConfig) => typeof pointCanvasConfig?.positive === 'boolean';

export function convertUIRegionItemToPointCanvasConfig(item: UiRegionItem, media: HTMLImageElement | HTMLVideoElement): PointCanvasConfig {
  const { regionData, key } = item;
  const color = regionData.color || '#000000';
  const point = (regionData as CF.API.PointShapeProps).point;

  const { naturalHeight, naturalWidth, videoHeight, videoWidth } = media as {
    naturalHeight?: number;
    naturalWidth?: number;
    videoHeight?: number;
    videoWidth?: number;
  };

  // TODO test with video labeling
  const height = naturalHeight || videoHeight || media.height;
  const width = naturalWidth || videoWidth || media.width;

  const x = point.x * width;
  const y = point.y * height;
  const radius = 5;

  return {
    key,
    x,
    y,
    radius,
    color,
  };
}

type ConvertPositivePointToPointCanvasConfigProps = {
  item: UiRegionItem;
  media: HTMLImageElement | HTMLVideoElement;
  positive: boolean;
};

export function convertPositivePointToPointCanvasConfig({ item, media, positive }: ConvertPositivePointToPointCanvasConfigProps): PointCanvasConfig {
  const { regionData, key } = item;
  const color = positive ? 'green' : 'red'; // TODO: this is repeating, need to unify

  const point = (regionData as CF.API.PointShapeProps).point;

  const { naturalHeight, naturalWidth, videoHeight, videoWidth } = media as {
    naturalHeight?: number;
    naturalWidth?: number;
    videoHeight?: number;
    videoWidth?: number;
  };

  const height = naturalHeight || videoHeight || media.height;
  const width = naturalWidth || videoWidth || media.width;

  const x = point.x * width;
  const y = point.y * height;
  const radius = 5;

  return {
    key,
    x,
    y,
    radius,
    color,
    positive,
  };
}

function calculateNormalizedPoint(
  point: { x: number; y: number },
  display: { displayWidth?: number; displayHeight?: number },
  media: { mediaWidth: number; mediaHeight: number; scale: number },
): { x: number; y: number } {
  const { displayWidth, displayHeight } = display;
  const { mediaWidth, mediaHeight, scale } = media;

  if (displayWidth && displayHeight) {
    return {
      x: point.x / displayWidth,
      y: point.y / displayHeight,
    };
  }

  const naturalX = point.x / scale;
  const naturalY = point.y / scale;

  return {
    x: naturalX / mediaWidth,
    y: naturalY / mediaHeight,
  };
}

export function convertPointShapeToUIRegionItem(
  key: string,
  color: string,
  concepts: CF.API.Concept[],
  media: HTMLImageElement | HTMLVideoElement,
  shapeProps: PointShapeProps,
  scale: number = 1,
  displayWidth?: number,
  displayHeight?: number,
): UiRegionItem {
  const { naturalHeight, naturalWidth, videoHeight, videoWidth } = media as {
    naturalHeight?: number;
    naturalWidth?: number;
    videoHeight?: number;
    videoWidth?: number;
  };

  const mediaWidth = naturalWidth || videoWidth || media.width;
  const mediaHeight = naturalHeight || videoHeight || media.height;

  const normalizedPoint = calculateNormalizedPoint(shapeProps.point, { displayWidth, displayHeight }, { mediaWidth, mediaHeight, scale });

  return {
    key,
    concepts,
    regionData: {
      point: normalizedPoint,
      color,
    },
  };
}
